import axios from 'axios';
import cx from 'clsx';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useCallback, useRef, useState } from 'react';
import validator from 'validator';

import { useUIContext } from 'features/UIContext';
import { useT } from 'i18n';
import ActionGroup from '@placekit/uikit/components/ActionGroup';
import Button from '@placekit/uikit/components/Button';
import Form from '@placekit/uikit/components/Form';
import FormField from '@placekit/uikit/components/FormField';
import Icon from '@placekit/uikit/components/Icon';
import Input from '@placekit/uikit/components/Input';
import Spinner from '@placekit/uikit/components/Spinner';

import TurnstileField, { getTurnstileToken } from 'components/TurnstileField';

const Subscribe = (props) => {
  const { notify } = useUIContext();
  const t = useT();
  const [loading, setLoading] = useState(false);
  // The challenge only runs for visitors who actually touch the form, so a
  // homepage view costs no Cloudflare request.
  const [armed, setArmed] = useState(false);
  const turnstileRef = useRef(null);

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const onSubmit = useCallback(
    async (values) => {
      setLoading(true);
      try {
        const turnstileToken = await getTurnstileToken(turnstileRef);
        await axios.post(
          process.env.NEXT_PUBLIC_SUBSCRIBE_ENDPOINT,
          {
            ...values,
            list: props.list,
            turnstileToken,
          },
          {
            headers: {
              'x-token': `${process.env.NEXT_PUBLIC_SUBSCRIBE_TOKEN}`,
            },
          },
        );
        notify({
          kind: 'success',
          icon: 'check-badge',
          delay: 6000,
          title: t('subscribe.successTitle'),
          message: t('subscribe.successMessage'),
        });
        methods.reset();
      } catch (err) {
        notify({
          kind: 'danger',
          icon: 'exclamation-triangle',
          delay: 6000,
          title: t('subscribe.errorTitle'),
          message: err.message,
        });
      } finally {
        setLoading(false);
        // Turnstile tokens are single use: ask for a fresh challenge.
        turnstileRef.current?.reset();
      }
    },
    [methods, notify, props, t],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      id={`${props.list}-subscribe-form`}
      onFocusCapture={() => setArmed(true)}
      onPointerDown={() => setArmed(true)}
      className={cx(
        'inline-flex flex-col gap-2 p-4 rounded-lg border border-gray-200 bg-white shadow-md shadow-gray-800/5',
        props.className,
      )}
    >
      <div className="flex items-center gap-3 rounded-lg bg-accent-50 p-1 pr-4">
        <span
          className="inline-flex p-2 rounded-md bg-accent-100 text-accent-700"
          aria-hidden="true"
        >
          <Icon name="bell" />
        </span>
        <h4 className="leading-none font-semibold text-accent-600 text-md text-left">
          {props.title || t('subscribe.defaultTitle')}
        </h4>
      </div>
      <FormField
        name="name"
        label={t('subscribe.nameLabel')}
        discrete={true}
        autoComplete="off"
        tabIndex="-1"
        render={({ errored, ...fieldProps }) => <Input {...fieldProps} className="hidden!" />}
        className="col-span-full h-0 overflow-hidden"
      />
      {/* The widget shares this wrapper with the email row instead of being a
          column item, so its invisible state adds no `gap-2` to the card. */}
      <div>
        <ActionGroup stack={false} fullWidth={true}>
          <FormField
            name="email"
            label={t('subscribe.emailLabel')}
            type="email"
            discrete={true}
            placeholder={t('subscribe.emailPlaceholder')}
            rules={{
              validate: (val) => validator.isEmail(val),
            }}
            icon="at-symbol"
            render={(fieldProps) => <Input {...fieldProps} />}
          />
          <Button type="submit" kind="primary" disabled={loading}>
            {!loading ? t('subscribe.send') : <Spinner />}
          </Button>
        </ActionGroup>
        {armed && <TurnstileField ref={turnstileRef} id="turnstile-subscribe" />}
      </div>
    </Form>
  );
};

Subscribe.propTypes = {
  title: PropTypes.string,
  list: PropTypes.string.isRequired,
};

export default Subscribe;
